import moment from 'moment';
import momentTZ from 'moment-timezone';
import {captureSentryMessage} from 'src/utils/sentry';
import logger from 'src/utils/logger';
import {format as formatDateFns} from 'date-fns';
import {localeFormats, type LocaleKey} from 'src/constants/language-support';

// @ts-expect-error TS2304
export function wrangleMoment(date?: string | Date | moment$Moment): Date {
  let parsedDate;
  if (date instanceof Date) {
    parsedDate = date;
  } else if (!date) {
    parsedDate = new Date();
  } else {
    try {
      if (moment.isMoment(date)) {
        parsedDate = (date as moment.Moment).toDate();
      } else if (typeof date === 'string') {
        parsedDate = moment(date).toDate();
      } else if (typeof date === 'number') {
        parsedDate = moment(date).toDate();
      } else {
        parsedDate = new Date();
      }
    } catch (error: any) {
      captureSentryMessage(`Error parsing date: ${error.message}`, {
        level: 'error',
        tags: {product: 'scheduler', inputDate: date},
      });
      logger.warn('Error parsing date', {date, error});
      return new Date();
    }
  }

  // Check if the parsedDate is valid
  if (isNaN(parsedDate.getTime())) {
    captureSentryMessage(`Invalid date: ${parsedDate.getTime()}`, {
      level: 'error',
      tags: {product: 'scheduler', parsedDate},
    });
    logger.warn('Invalid Date', date);
    return new Date();
  }

  return parsedDate;
}

// ====================================================================================================
// Timezone-Aware Date Utilities using moment-timezone
// ====================================================================================================

/**
 * Creates a Date object in the specified timezone.
 *
 * @param date - Date input (string, number, Date, or Moment)
 * @param timezone - IANA timezone string (e.g., 'America/New_York')
 * @returns Date object in the specified timezone
 */
export const createTZDate = (
  date: string | number | Date | moment.Moment,
  timezone: string,
): Date => {
  const parsedDate = wrangleMoment(date);
  return momentTZ.tz(parsedDate, timezone).toDate();
};

/**
 * Adds days to a timezone-aware date.
 *
 * @param date - Date input (string, number, Date, or Moment)
 * @param days - Number of days to add
 * @param timezone - IANA timezone string
 * @returns New Date object with days added
 */
export const addTZDays = (
  date: string | number | Date | moment.Moment,
  days: number,
  timezone: string,
): Date => {
  const parsedDate = wrangleMoment(date);
  return momentTZ.tz(parsedDate, timezone).add(days, 'days').toDate();
};

/**
 * Subtracts days from a timezone-aware date.
 *
 * @param date - Date input (string, number, Date, or Moment)
 * @param days - Number of days to subtract
 * @param timezone - IANA timezone string
 * @returns New Date object with days subtracted
 */
export const subTZDays = (
  date: string | number | Date | moment.Moment,
  days: number,
  timezone: string,
): Date => {
  const parsedDate = wrangleMoment(date);
  return momentTZ.tz(parsedDate, timezone).subtract(days, 'days').toDate();
};

/**
 * Checks if date1 is before date2 in the same timezone.
 *
 * @param date1 - First date input
 * @param date2 - Second date input
 * @param timezone - IANA timezone string
 * @returns True if date1 is before date2
 */
export const isTZBefore = (
  date1: string | number | Date | moment.Moment,
  date2: string | number | Date | moment.Moment,
  timezone: string,
): boolean => {
  const parsedDate1 = wrangleMoment(date1);
  const parsedDate2 = wrangleMoment(date2);
  return momentTZ
    .tz(parsedDate1, timezone)
    .isBefore(momentTZ.tz(parsedDate2, timezone));
};

/**
 * Checks if date1 is after date2 in the same timezone.
 *
 * @param date1 - First date input
 * @param date2 - Second date input
 * @param timezone - IANA timezone string
 * @returns True if date1 is after date2
 */
export const isTZAfter = (
  date1: string | number | Date | moment.Moment,
  date2: string | number | Date | moment.Moment,
  timezone: string,
): boolean => {
  const parsedDate1 = wrangleMoment(date1);
  const parsedDate2 = wrangleMoment(date2);
  return momentTZ
    .tz(parsedDate1, timezone)
    .isAfter(momentTZ.tz(parsedDate2, timezone));
};

/**
 * Checks if date1 is equal to date2 in the same timezone.
 *
 * @param date1 - First date input
 * @param date2 - Second date input
 * @param timezone - IANA timezone string
 * @returns True if dates are equal
 */
export const isTZEqual = (
  date1: string | number | Date | moment.Moment,
  date2: string | number | Date | moment.Moment,
  timezone: string,
): boolean => {
  const parsedDate1 = wrangleMoment(date1);
  const parsedDate2 = wrangleMoment(date2);
  return momentTZ
    .tz(parsedDate1, timezone)
    .isSame(momentTZ.tz(parsedDate2, timezone));
};

/**
 * Gets the start of day in the specified timezone.
 *
 * @param date - Date input (string, number, Date, or Moment)
 * @param timezone - IANA timezone string
 * @returns Date object at start of day in timezone
 */
export const startOfTZDay = (
  date: string | number | Date | moment.Moment,
  timezone: string,
): Date => {
  const parsedDate = wrangleMoment(date);
  return momentTZ.tz(parsedDate, timezone).startOf('day').toDate();
};

/**
 * Gets the start of week in the specified timezone.
 *
 * @param date - Date input (string, number, Date, or Moment)
 * @param timezone - IANA timezone string
 * @param weekStartsOn - 0 = Sunday, 1 = Monday (default: 1)
 * @returns Date object at start of week in timezone
 */
export const startOfTZWeek = (
  date: string | number | Date | moment.Moment,
  timezone: string,
  weekStartsOn: 0 | 1 = 1,
): Date => {
  const parsedDate = wrangleMoment(date);
  const momentDate = momentTZ.tz(parsedDate, timezone);
  if (weekStartsOn === 1) {
    return momentDate.startOf('isoWeek').toDate(); // Monday
  }
  return momentDate.startOf('week').toDate(); // Sunday
};

/**
 * Formats a date as ISO 8601 string (works with Moment types).
 * Uses moment for ISO formatting
 *
 * @param date - Date input (string, number, Date, or Moment)
 * @param options - Format options
 * @returns ISO 8601 formatted string
 */
export const formatISO = (
  date: string | number | Date | moment.Moment,
  options?: {representation?: 'complete' | 'date' | 'time'},
): string => {
  const parsedDate = wrangleMoment(date);
  const momentDate = moment(parsedDate);

  if (options?.representation === 'date') {
    return momentDate.format('YYYY-MM-DD');
  }
  if (options?.representation === 'time') {
    return momentDate.format('HH:mm:ss');
  }
  if (options?.representation === 'complete') {
    return momentDate.format('YYYY-MM-DD[T]HH:mm:ss');
  }
  return momentDate.toISOString();
};

/**
 * Parses a time string (e.g., 'HH:mm') into a Date object.
 *
 * @param time - Time string to parse
 * @param format - Format string (e.g., 'HH:mm')
 * @param referenceDate - Reference date (defaults to today)
 * @returns Date object with parsed time
 */
export const parseTime = (
  time: string,
  format: string,
  referenceDate?: Date,
): Date => {
  const ref = referenceDate || new Date();
  const dateStr = moment(ref).format('YYYY-MM-DD');
  return moment(`${dateStr} ${time}`, `YYYY-MM-DD ${format}`).toDate();
};

/**
 * Adds minutes to a date.
 *
 * @param date - Date input (string, number, Date, or Moment)
 * @param amount - Number of minutes to add
 * @returns New Date object with minutes added
 */
export const addMinutes = (
  date: string | number | Date | moment.Moment,
  amount: number,
): Date => {
  const parsedDate = wrangleMoment(date);
  return moment(parsedDate).add(amount, 'minutes').toDate();
};

export const addDays = (
  date: string | number | Date | moment.Moment,
  days: number,
): Date => {
  const parsedDate = wrangleMoment(date);
  return moment(parsedDate).add(days, 'days').toDate();
};

export const subtractDays = (
  date: string | number | Date | moment.Moment,
  days: number,
): Date => {
  const parsedDate = wrangleMoment(date);
  return moment(parsedDate).subtract(days, 'days').toDate();
};

// ====================================================================================================
// Date formatting functions using Intl.DateTimeFormat
// ====================================================================================================

/**
 * Gets the browser timezone.
 * @returns Browser timezone
 */
export const getBrowserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Gets the timezone name for a given date.
 * @param date - Date input (string, number, Date, or Moment)
 * @param language - Locale language (default: 'en')
 * @returns Timezone name
 */

export const timezoneName = (
  date: string | number | Date | moment.Moment,
  language: string,
) => {
  const parsedDate = wrangleMoment(date);

  return new Intl.DateTimeFormat(language, {
    timeZoneName: 'long',
  })
    .formatToParts(parsedDate)
    .find((part) => part.type === 'timeZoneName')?.value;
};

/*
 * Formats a date using Intl.DateTimeFormat with locale support.
 * @param date - Date input (string, number, Date, or Moment)
 * @param language - Locale language (default: 'en')
 * @param formatOptions - Format options (default: {year: 'numeric', month: 'short', day: 'numeric'})
 * @returns Formatted date string
 */

export const formatWithIntl = (
  date: string | number | Date | moment.Moment,
  language: string = 'en',
  formatOptions?: Intl.DateTimeFormatOptions,
) => {
  return new Intl.DateTimeFormat(language, formatOptions).format(
    wrangleMoment(date),
  );
};

/**
 * Displays a date with full date format (month abbreviation, day, and year).
 * @example
 * displayDate('1995-12-09')
 * // Returns: "Dec 9, 1995"
 *
 * displayDate('2023-03-15', 'fr')
 * // Returns: "15 mars 2023"
 *
 * displayDate(new Date(2023, 5, 20))
 * // Returns: "Jun 20, 2023"
 */
// ex Dec 9, 1995
export function displayDate(
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
  language: string = 'en',
): string {
  return formatWithIntl(date, language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Displays a date with full date and time format (weekday, month, day, year, hour, minute, AM/PM).
 * @example
 * displayFullDateTime('1995-12-09')
 * // Returns: "Monday, December 9, 1995"
 *
 * displayFullDateTime('2023-03-15', 'fr')
 * // Returns: "Friday, March 15, 2023"
 *
 * displayDate(new Date(2023, 5, 20))
 * // Returns: "Tuesday, June 20, 2023"
 */

export function displayFullDateTime(
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
  language: string = 'en',
): string {
  return formatWithIntl(date, language, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Displays only the time portion of a date.
 * @example
 * displayTime('2023-12-15T14:30:00Z')
 * // Returns: "2:30 PM"
 *
 * displayTime('2023-12-15T09:05:00Z', 'en-GB')
 * // Returns: "09:05"
 *
 * displayTime(new Date(2023, 11, 15, 18, 45), 'en-US', { hour12: false })
 * // Returns: "18:45"
 */
export function displayTime(
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
  language: string = 'en',
): string {
  return formatWithIntl(date, language, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Displays the week in short format (day of the week).
 * @example
 * displayWeekInShortFormat('2023-12-15')
 * // Returns: "Fri"
 *
 * displayWeekInShortFormat('2023-12-15', 'fr')
 * // Returns: "ven."
 */
export function displayWeekInShortFormat(
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
  language?: string,
): string {
  return formatWithIntl(date, language, {weekday: 'short'});
}

/**
 * Displays only the day number of a date.
 * @example
 * displayDateWithShortFormat('2023-12-15')
 * // Returns: "15"
 *
 * displayDateWithShortFormat('2023-12-05')
 * // Returns: "5"
 *
 * displayDateWithShortFormat('2023-12-15', 'fr')
 * // Returns: "15"
 */
export function displayDateWithShortFormat(
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
  language: string = 'en',
): string {
  return formatWithIntl(date, language, {
    day: 'numeric',
  });
}

/**
 * Displays a date with full weekday, full month, day, and year format (Monday, January 20, 2026).
 * @example
 * displayDateWithWeekday('2026-01-20')
 * // Returns: "Monday, January 20, 2026"
 *
 * displayDateWithWeekday('2023-12-15', 'fr')
 * // Returns: "vendredi 15 décembre 2023"
 *
 * displayDateWithWeekday(new Date(2023, 5, 20))
 * // Returns: "Tuesday, June 20, 2023"
 */
export function displayDateWithWeekday(
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
  language: string = 'en',
): string {
  return formatWithIntl(date, language, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Displays a date with ordinal day and month abbreviation (e.g., "21st Jan").
 * @example
 * displayDateWithOrdinal('2026-01-21', 'en') // Returns: "21st Jan"
 * displayDateWithOrdinal(new Date(2026, 0, 2), 'en') // Returns: "2nd Jan"
 */
export const displayDateWithOrdinal = (
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
  language: string = 'en',
): string => {
  const parsedDate = wrangleMoment(date);
  const localeConfig = localeFormats[language as LocaleKey] || localeFormats.en;
  return formatDateFns(parsedDate, 'do MMM', {locale: localeConfig.locale});
};
